import { WabunkaDestinationTagGroupListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_tag';

import { useAsyncFetchFromPublicWabunkaApi } from '@/wabunka-src/common/composables/query/useAsyncFetchFromPublicWabunkaApi';
import type { FeaturedDestinationGroupViewModel } from '@/wabunka-src/common/features/Featured/core/featuredDestinations/featuredDestinationsMap';

/**
 * 行き先タググループを取得する Composable。一覧マップのエリアラベル層が使う。
 * Page でのみ使用可能。
 *
 * backend の `GET /public/v1/consumer/destination-tag-groups`（JCAT-5626）を叩き、
 * 地理タググループを web_path・メンバータグ・F-27 件数付きで返す。件数は backend で日次
 * キャッシュ済み。
 *
 * 装飾的な用途のため `throwOnError: false` で graceful degradation する。RPC 失敗時は
 * 空配列を返し、エリアラベルが出ないだけでページ全体は落とさない。
 * これに伴い WabunkaError 形式の API エラー（backend 整形の 4xx/5xx）は throw も Sentry 送出も
 * されず無言で degrade する。失敗は返り値の `hasErrored` で呼び出し側が拾う。
 * （network/unexpected エラーは base composable 側で Sentry に送出される）
 */

interface UseFeaturedDestinationsQueryOptions {
  /**
   * setup 時に即取得するか。既定 true（SSR で取得する）。
   *
   * false にすると SSR を行わず、返り値の `execute()` を呼ぶまで取得しない。一覧マップの
   * エリアラベル層のように「特定のズームまでズームアウトした人だけが必要とする」用途で使う
   * （到達しないユーザーにリクエストを払わせないため）。
   */
  immediate?: boolean;
}

export const useFeaturedDestinationsQuery = async ({ immediate = true }: UseFeaturedDestinationsQueryOptions = {}) => {
  const path = computed(() => '/destination-tag-groups');

  const { data, status, refresh } = await useAsyncFetchFromPublicWabunkaApi({
    path,
    options: { server: immediate, immediate },
    converter: WabunkaDestinationTagGroupListResponse.fromJSON,
    throwOnError: false,
  });

  const destinationGroups = computed<FeaturedDestinationGroupViewModel[]>(() =>
    (data.value?.items ?? []).map((item) => ({
      webPath: item.webPath,
      name: item.name,
      experienceCount: item.experienceCount,
      tagWebPaths: item.tagWebPaths,
    })),
  );

  /** 取得に失敗したか。`refresh()` は reject しないため、呼び出し側はこれで失敗を知る。 */
  const hasErrored = computed<boolean>(() => status.value === 'error');

  return { destinationGroups, hasErrored, execute: refresh };
};
